import React from 'react';
import { gradePrices, GRADE_COLORS } from '../../utils/priceColor';

const COMPANIES = ['musicmagpie', 'webuybooks', 'worldofbooks', 'cex_cash', 'cex_voucher'];

/**
 * Full barcode detail modal — metrics, per-company editor (with cash-equivalent colour coding),
 * and price-check history. Everything needed to audit and correct one library entry.
 */
export default function BarcodeDetailModal({
  selectedBarcode,
  detail,
  editing,
  editValue,
  savingEdit,
  setEditValue,
  setEditing,
  onSave,
  onDelete,
  onClose,
}) {
  if (!selectedBarcode) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      data-testid="barcode-detail-modal"
    >
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#FFD700] font-mono">{selectedBarcode}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="detail-close-btn">✕</button>
        </div>

        <div className="p-4 space-y-4">
          {!detail ? (
            <p className="text-zinc-500">Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-zinc-950 p-3 rounded">
                  <div className="text-zinc-500 text-xs">Scans</div>
                  <div className="text-2xl font-bold">{detail.scan_count}</div>
                </div>
                <div className="bg-zinc-950 p-3 rounded">
                  <div className="text-zinc-500 text-xs">Unique scanners</div>
                  <div className="text-2xl font-bold">{detail.unique_scanners}</div>
                </div>
                <div className="bg-zinc-950 p-3 rounded">
                  <div className="text-zinc-500 text-xs">Price checks</div>
                  <div className="text-2xl font-bold">{detail.price_checks?.length ?? 0}</div>
                </div>
              </div>

              {detail.library_entry ? (
                <DetailEditor
                  entry={detail.library_entry}
                  selectedBarcode={selectedBarcode}
                  editing={editing}
                  editValue={editValue}
                  savingEdit={savingEdit}
                  setEditValue={setEditValue}
                  setEditing={setEditing}
                  onSave={onSave}
                  onDelete={onDelete}
                />
              ) : (
                <p className="text-zinc-500 text-sm">
                  No library entry for this barcode (scan-only so far — no prices submitted).
                </p>
              )}

              <div>
                <h3 className="text-sm font-semibold text-zinc-300 mb-2">Price checks ({detail.price_checks?.length ?? 0})</h3>
                <div className="bg-zinc-950 p-3 rounded text-xs max-h-80 overflow-y-auto">
                  {(detail.price_checks || []).map((c) => (
                    <div key={`${c.checked_at || ''}-${c.company}-${c.price}`} className="flex justify-between py-1 border-b border-zinc-900 last:border-0">
                      <span className="text-zinc-400">{(c.checked_at || '').slice(0, 19).replace('T', ' ')}</span>
                      <span className="font-mono">{c.company}</span>
                      <span className="font-mono text-[#00D26A]">£{Number(c.price).toFixed(2)}</span>
                      <span className={c.status === 'verified' ? 'text-[#00D26A]' : 'text-zinc-500'}>{c.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailEditor({ entry, selectedBarcode, editing, editValue, savingEdit, setEditValue, setEditing, onSave, onDelete }) {
  const { grades } = gradePrices(entry?.prices || {});

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-zinc-300">Current prices (click a row to edit / delete)</h3>
        <div className="text-[10px] text-zinc-500">
          <span style={{ color: '#FFD700' }}>●</span> Best &nbsp;
          <span style={{ color: '#00D26A' }}>●</span> Viable (≥60%) &nbsp;
          <span style={{ color: '#E04A3F' }}>●</span> Decline
        </div>
      </div>
      <div className="bg-zinc-950 rounded border border-zinc-800 divide-y divide-zinc-800" data-testid="library-prices-editor">
        {COMPANIES.map((company) => {
          const priceEntry = entry?.prices?.[company];
          const value = priceEntry?.value;
          const exists = value !== undefined && value !== null;
          const isEditing = editing === company;
          const g = GRADE_COLORS[grades[company]] || GRADE_COLORS.grey;

          return (
            <div key={company} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`price-row-${company}`}>
              <span aria-hidden className="flex-shrink-0 w-1 h-6 rounded-full" style={{ background: g.accent }} />
              <span className="w-28 text-zinc-400 font-mono text-xs">{company}</span>
              {isEditing ? (
                <>
                  <span className="text-zinc-500 text-xs">£</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    defaultValue={exists ? Number(value).toFixed(2) : '0.00'}
                    autoFocus
                    onChange={(e) => setEditValue(e.target.value)}
                    className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-[#00D26A] font-mono w-28"
                    data-testid={`edit-input-${company}`}
                  />
                  <button
                    onClick={() => onSave(selectedBarcode, company)}
                    disabled={savingEdit}
                    className="px-3 py-1 bg-[#00D26A] text-black text-xs font-semibold rounded hover:brightness-110 disabled:opacity-50"
                    data-testid={`save-${company}`}
                  >{savingEdit ? 'Saving…' : 'Save'}</button>
                  <button
                    onClick={() => { setEditing(null); setEditValue(''); }}
                    className="px-3 py-1 text-xs text-zinc-400 hover:text-white"
                    data-testid={`cancel-${company}`}
                  >Cancel</button>
                </>
              ) : (
                <>
                  <span className="flex-1 font-mono" style={{ color: exists ? g.text : '#6a6a6a' }}>
                    {exists ? `£${Number(value).toFixed(2)}` : '— (no entry)'}
                  </span>
                  <button
                    onClick={() => { setEditing(company); setEditValue(exists ? String(value) : '0'); }}
                    className="px-3 py-1 text-xs text-[#00D26A] hover:bg-zinc-900 rounded"
                    data-testid={`edit-${company}`}
                  >Edit</button>
                  {exists && (
                    <button
                      onClick={() => onDelete(selectedBarcode, company)}
                      disabled={savingEdit}
                      className="px-3 py-1 text-xs text-red-400 hover:bg-red-950/40 rounded disabled:opacity-50"
                      data-testid={`delete-${company}`}
                    >Delete</button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
